import type { SVGProps } from 'react';
import { cx } from '../ui/cx.ts';

/**
 * Nadoutong tenant-console brand — Dispatch Deck family (epic #897).
 *
 * The mark is a dispatch fan-out: one signal-orange hub routing to
 * three paper terminal lamps (square, echoing `StatusLamp`). It
 * replaces the pre-#897 blue/purple gradient artwork and reads clearly
 * down to rail size (32px), where the Shell renders `BrandMark` alone —
 * same approach as the ops-console `BrandLogo` icon-only compact mode.
 *
 * Hex literals in this file are the single documented exception to the
 * "no hex in .tsx" guardrail (design-guardrails test): brand artwork
 * colors echo the Deck palette (`signal` #E08D5D, `signal-bright`
 * #EBA173, `paper` #EDE6DC — see src/ui/tokens.ts) but live in the SVG
 * itself so the asset survives outside a Tailwind context.
 */

interface Props {
  readonly className?: string;
  readonly descriptor?: string;
  readonly testId?: string;
}

export function BrandMark(props: SVGProps<SVGSVGElement>): JSX.Element {
  return (
    <svg viewBox="0 0 48 48" fill="none" aria-hidden="true" {...props}>
      {/* hub */}
      <circle cx="10" cy="24" r="5.5" fill="#E08D5D" />
      {/* routes */}
      <path
        d="M15 21C22 13 29 11.5 36 10.5"
        stroke="#E08D5D"
        strokeWidth="3.5"
        strokeLinecap="round"
      />
      <path d="M16.5 24H36" stroke="#EBA173" strokeWidth="3.5" strokeLinecap="round" />
      <path
        d="M15 27C22 35 29 36.5 36 37.5"
        stroke="#E08D5D"
        strokeWidth="3.5"
        strokeLinecap="round"
        opacity="0.72"
      />
      {/* terminal lamps */}
      <rect x="38" y="7" width="7" height="7" rx="1.8" fill="#EDE6DC" />
      <rect x="38" y="20.5" width="7" height="7" rx="1.8" fill="#EDE6DC" />
      <rect x="38" y="34" width="7" height="7" rx="1.8" fill="#EDE6DC" />
    </svg>
  );
}

export function BrandWordmark({ className, descriptor, testId }: Props): JSX.Element {
  return (
    <div className={cx('flex items-center gap-2.5', className)} data-testid={testId}>
      <BrandMark className="h-9 w-9 shrink-0" />
      <div className="flex min-w-0 flex-col">
        <span className="block truncate text-title-lg text-paper">哪都通</span>
        {descriptor !== undefined && descriptor.length > 0 ? (
          <span className="mt-0.5 block truncate text-micro font-medium text-signal">
            {descriptor}
          </span>
        ) : null}
      </div>
    </div>
  );
}
